import { PortalProductsSubscriptionsStore } from './portal-products-subscriptions.store';
import { Injectable } from '@angular/core';
import { PortalProductSubscription } from '../portal-products-subscriptions/models/portal-product-subscription.model';
import { ApiService } from '../../shared/services/core/api.service';
import { LoggingService } from '../../shared/services/core/logging.service';
import { Observable, of } from 'rxjs';
import { tap, mapTo } from 'rxjs/operators';
import { PortalProductsSubscriptionsQuery } from './portal-products-subscriptions.query';

/**
 * This service will handle fetching the
 * portal products which have been purchased by
 * a member and setting the store.
 * This service also includes the call to reset
 * the subscribed portal products store.
 */

@Injectable({ providedIn: 'root' })
export class PortalProductsSubscriptionsService {
  constructor(
    private portalProductsSubscriptionsStore: PortalProductsSubscriptionsStore,
    private portalProductsSubscriptionsQuery: PortalProductsSubscriptionsQuery,
    private logService: LoggingService,
    private api: ApiService
  ) {}

  /**
   * @returns This method gets the subscribed portal
   * products data and sets the store. This will be
   * called within the resolver.
   */
  public fetchSubscriptions() {
    this.portalProductsSubscriptionsStore.setLoading(true);
    this.logService.info('making call for user portal subscriptions');
    const request$: Observable<null> = this.api.get<PortalProductSubscription>('subscriptions').pipe(
      tap((subscriptions) => this.portalProductsSubscriptionsStore.set(subscriptions)),
      mapTo(null)
    );
    return this.portalProductsSubscriptionsQuery.getHasCache() ? of(null) : request$;
  }

  /**
   * @returns Activates a subscription by ID.
   */
  public activateSubscription(subscriptionId: number): Observable<null> {
    this.logService.info(`activating subscription ${subscriptionId}`);
    return this.api.post<null>(`subscriptions/${subscriptionId}/activate`, null).pipe(
      tap(() => this.portalProductsSubscriptionsStore.setLoading(false)),
      mapTo(null)
    );
  }

  /**
   * @returns Deactivates a subscription by ID.
   */
  public deactivateSubscription(subscriptionId: number): Observable<null> {
    this.logService.info(`deactivating subscription ${subscriptionId}`);
    return this.api.post<null>(`subscriptions/${subscriptionId}/deactivate`, null).pipe(
      mapTo(null)
    );
  }

  /**
   * @returns This method is called to reset
   * the subscribed portal products store.
   */
  clear() {
    this.portalProductsSubscriptionsStore.reset();
  }
}
